if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.bindGallery();
      }

      connectedCallback() {
        this.bindGallery();
      }

      bindGallery() {
        if (this.galleryBound) return;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          loading: this.querySelector('.product-gallery__loading'),
        };

        if (!this.elements.viewer) return;

        this.galleryBound = true;
        this.variantMediaMap = this.parseVariantMediaMap();

        this.elements.prev = this.querySelector('.product__media-nav--prev');
        this.elements.next = this.querySelector('.product__media-nav--next');
        this.elements.prev?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.stepMedia(-1);
        });
        this.elements.next?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.stepMedia(1);
        });
        this.snapActiveMedia();
        this.mql.addEventListener('change', () => this.snapActiveMedia());

        this.elements.viewer.querySelectorAll('[data-media-key]').forEach((slide) => {
          slide.addEventListener('click', (event) => {
            if (event.target.closest('a, product-model, deferred-media')) return;
            this.selectVariantForMedia(slide);
          });
        });

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button')?.addEventListener('click', (event) => {
            event.preventDefault();
            this.setActiveMedia(mediaToSwitch.dataset.target, false);
            this.selectVariantForMedia(mediaToSwitch);
          });
        });
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      snapActiveMedia() {
        if (this.mql.matches) return;
        const slider = this.elements?.viewer?.querySelector('.product__media-list');
        const active = slider?.querySelector('.product__media-item.is-active');
        if (!slider || !active) return;
        requestAnimationFrame(() => {
          slider.scrollTo({ left: active.offsetLeft, behavior: 'auto' });
        });
      }

      stepMedia(direction) {
        const slides = Array.from(this.elements.viewer.querySelectorAll('.product__media-item'));
        if (slides.length < 2) return;

        const currentIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
        const nextIndex = (Math.max(currentIndex, 0) + direction + slides.length) % slides.length;
        const nextSlide = slides[nextIndex];
        if (!nextSlide?.dataset?.mediaId) return;

        this.setActiveMedia(nextSlide.dataset.mediaId, false);
      }

      parseVariantMediaMap() {
        const script = this.querySelector('[data-variant-media-map]');
        if (!script) return [];
        try {
          const parsed = JSON.parse(script.textContent);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }

      selectVariantForMedia(mediaElement) {
        const mediaKey = mediaElement?.dataset?.mediaKey;
        const imageId = mediaElement?.dataset?.imageId;
        const matchingVariants = (this.variantMediaMap || []).filter((variant) => {
          const ids = [variant.mediaId, variant.imageId].filter(Boolean).map(String);
          return ids.includes(String(mediaKey)) || ids.includes(String(imageId));
        });
        if (!matchingVariants.length) return;

        const optionValueIds = matchingVariants[0].optionValueIds || [];
        if (!optionValueIds.length) return;

        const productInfo = this.closest('product-info') || document.querySelector('product-info');
        const variantSelects = productInfo?.querySelector('variant-selects');
        if (!variantSelects) return;

        const alreadySelected = optionValueIds.every((id) => {
          const input = variantSelects.querySelector(`[data-option-value-id="${id}"]`);
          if (!input) return false;
          if (input.tagName === 'OPTION') return input.selected;
          return input.checked;
        });
        if (alreadySelected) return;

        let changeTarget = null;

        optionValueIds.forEach((id) => {
          const input = variantSelects.querySelector(`input[data-option-value-id="${id}"]`);
          if (input) {
            input.checked = true;
            changeTarget = input;
            return;
          }

          const option = variantSelects.querySelector(`select option[data-option-value-id="${id}"]`);
          if (!option) return;

          const select = option.closest('select');
          select.querySelectorAll('option[selected]').forEach((selectedOption) => {
            selectedOption.removeAttribute('selected');
          });
          option.setAttribute('selected', 'selected');
          select.value = option.value;
          changeTarget = select;
        });

        if (!changeTarget) return;

        changeTarget.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onSlideChanged(event) {
        const current = event.detail.currentElement;
        if (current) {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
            element.classList.toggle('is-active', element === current);
          });
        }

        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        this.bindGallery();
        if (!this.elements?.viewer) return;

        const rawId = String(mediaId).includes('-') ? String(mediaId).split('-').pop() : String(mediaId);
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector(`[data-media-key="${rawId}"]`) ||
          this.elements.viewer.querySelector(`[data-image-id="${rawId}"]`) ||
          this.elements.viewer.querySelector(`[data-media-id$="-${rawId}"]`);
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
        mediaId = activeMedia.dataset.mediaId;

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: this.mql.matches ? 'smooth' : 'auto',
            });
          }
          if (!this.mql.matches) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.watchActiveImage(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset?.mediaPosition);
      }

      setVariantLoading(isLoading) {
        this.variantRequestPending = Boolean(isLoading);
        if (isLoading) {
          this.showLoading();
          return;
        }
        this.hideLoadingWhenReady();
      }

      showLoading() {
        this.elements?.loading?.classList.remove('hidden');
        this.classList.add('is-loading');
      }

      hideLoading() {
        this.elements?.loading?.classList.add('hidden');
        this.classList.remove('is-loading');
      }

      hideLoadingWhenReady() {
        if (this.variantRequestPending) return;
        const image = this.getActiveImage();
        if (image && !image.complete) return;
        this.hideLoading();
      }

      getActiveImage() {
        return this.elements?.viewer?.querySelector('.product__media-item.is-active img');
      }

      watchActiveImage(activeMedia) {
        const image = activeMedia?.querySelector('img');
        if (!image || image.complete) {
          this.hideLoadingWhenReady();
          return;
        }

        this.showLoading();
        const finish = () => this.hideLoadingWhenReady();
        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        if (!this.mql.matches) return;
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
